import * as React from "react";
import * as cn from "classnames/bind";
import { connect } from "react-redux";
import { withRouter, RouteComponentProps, Link } from "react-router-dom";
import Select from "react-select";
import * as Spinner from "react-spinkit";
import uuidv1 from "uuid/v1";
import { User, Role } from "appz";
import {
    fetchUser,
    fetchRoles,
    updateUser,
    addUser,
    deleteUser,
} from "page/user/service";
import { AdminHeader } from "page/admin/admin-header";
import * as styles from "./edit-user.css";
import * as buttonStyles from "../user.css";
import { ApplicationState } from "store";
import { getId } from "user/store";
import { getResource } from "resource/store";
import { useResources } from "contexts/resource-context";

const ConfirmationModal: React.FC<{
    isOpen: boolean;
    title: string;
    message: string;
    content: React.ReactNode;
}> = ({ isOpen, title, message, content }) => {
    return (
        <div className={`${styles.overlay} ${isOpen && styles.overlayActive}`}>
            <div className={styles.modal}>
                <div className={styles.modalTitle}>{title}</div>
                <div className={styles.modalMessage}>{message}</div>
                {content}
            </div>
        </div>
    );
};

const UserErrorMessage: React.SFC<{ errorKey: string }> = (props) => {
    const { errorKey } = props;
    const resources = useResources();

    return (
        <div className={"form-group"}>
            <div className={"text-danger"}>{resources[errorKey]}</div>
        </div>
    );
};

const cx = cn.bind(styles);

interface EditUserState {
    fetching: boolean;
    success: boolean;
    message: string | null;
    roles: Array<Role>;
    user: User | null;
    password: string;
    repeatedPassword: string;
    confirmSoftDelete: boolean;
    confirmDelete: boolean;
}

const centeredSpinnerCn = () =>
    cx({
        centeredSpinner: true,
    });

type Props = {
    loggedInUserId: string;
    Admin_Dashboard_Users: string;
    Admin_Dashboard_Users_Username: string;
    Admin_Dashboard_Users_Email: string;
    Admin_Dashboard_Users_Roles: string;
    Admin_Dashboard_Users_New: string;
    Admin_Dashboard_Users_Edit: string;
    Admin_Dashboard_Users_Password: string;
    Admin_Dashboard_Users_RepeatPassword: string;
    Admin_Dashboard_Users_Save: string;
    Admin_Dashboard_Users_SelectRoles: string;
    Admin_Dashboard_Users_Confirmation: string;
    Admin_Dashboard_Users_DeletionMessage: string;
    Admin_Dashboard_Users_InactivationMessage: string;
    Admin_Dashboard_Users_Confirm: string;
    Admin_Dashboard_Users_Cancel: string;
    Admin_Dashboard_Users_Delete: string;
    Admin_Dashboard_Users_Inactivate: string;
    Admin_Dashboard_Users_UserNotFound: string;
    Admin_Dashboard_Users_UserList: string;
    Admin_Dashboard_Users_UserWasSaved: string;
    Admin_Dashboard_Users_UserWasDeleted: string;
    Admin_Dashboard_Users_UserWasInactivated: string;
} & RouteComponentProps<{ id: string }>;

class EditUser extends React.PureComponent<Props> {
    public state: EditUserState = {
        fetching: true,
        success: true,
        message: null,
        roles: [],
        user: null,
        password: "",
        repeatedPassword: "",
        confirmSoftDelete: false,
        confirmDelete: false,
    };

    private getIndex = (): string => {
        return this.props.match.params.id;
    };

    private changeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: email } = e.target;

        this.setState((s: EditUserState) => ({
            user: {
                ...s.user,
                email,
            },
        }));
    };

    private changeUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value: userName } = e.target;

        this.setState((s: EditUserState) => ({
            user: {
                ...s.user,
                userName,
            },
        }));
    };

    private changePassword = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({
            password: e.target.value,
        });

    private changeRepeatedPassword = (e: React.ChangeEvent<HTMLInputElement>) =>
        this.setState({
            repeatedPassword: e.target.value,
        });

    private changeRoles = (roles: any) => {
        if (!Array.isArray(roles)) {
            return;
        }

        this.setState((s: EditUserState) => ({
            user: {
                ...s.user,
                roles: roles.map((r) => ({
                    name: r.label,
                    id: r.value,
                })),
            },
        }));
    };

    private changeConfirmSoftDelete = (
        e: React.MouseEvent<HTMLButtonElement>,
    ) => {
        e.preventDefault();
        this.setState({
            confirmSoftDelete: !this.state.confirmSoftDelete,
        });
    };

    private changeConfirmDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        this.setState({
            confirmDelete: !this.state.confirmDelete,
        });
    };

    private softDeleteUser = () => {
        this.setState(
            {
                message: null,
            },
            async () => {
                const { user } = this.state;

                if (user !== null) {
                    const guid = uuidv1();

                    const { success = false, message = "" } = await updateUser(
                        user.id,
                        guid + "_" + user.email,
                        guid + "_" + user.userName,
                        user.roles,
                        true,
                    );

                    if (success) {
                        this.props.history.push({
                            pathname: `/user/list`,
                            state: {
                                toastMessage: this.props
                                    .Admin_Dashboard_Users_UserWasInactivated,
                            },
                        });
                    } else {
                        this.setState({
                            message,
                        });
                        this.setState({
                            confirmSoftDelete: false,
                        });
                    }
                }
            },
        );
    };

    private deleteUser = async () => {
        if (this.state.user) {
            // cannot delete yourself
            if (this.state.user.id === this.props.loggedInUserId) {
                return;
            }

            const { success = false, message = "" } = await deleteUser(
                this.state.user.id,
            );

            if (success) {
                this.props.history.push({
                    pathname: `/user/list`,
                    state: {
                        toastMessage: this.props
                            .Admin_Dashboard_Users_UserWasDeleted,
                    },
                });
            } else {
                this.setState({
                    message,
                });
                this.setState({
                    confirmDelete: false,
                });
            }
        }
    };

    private onSubmit = (e: React.FormEvent<EventTarget>) => {
        e.preventDefault();

        this.setState(
            {
                message: null,
            },
            async () => {
                const { user, password, repeatedPassword } = this.state;

                if (user !== null) {
                    const isNewUser = !user.id;

                    const { success = false, message = "" } = await (isNewUser
                        ? addUser(
                              user.email,
                              user.userName,
                              user.roles,
                              password,
                              repeatedPassword,
                          )
                        : updateUser(
                              user.id,
                              user.email,
                              user.userName,
                              user.roles,
                              user.softDeleted,
                          ));

                    if (success) {
                        this.props.history.push({
                            pathname: `/user/list`,
                            state: {
                                toastMessage: this.props
                                    .Admin_Dashboard_Users_UserWasSaved,
                            },
                        });
                    } else {
                        this.setState({
                            message,
                        });
                    }
                }
            },
        );
    };

    public async componentDidMount() {
        const { success: rolesLoaded, data: roles } = await fetchRoles();
        const id = this.getIndex();

        // new user
        const newUser = id == null || parseInt(id) <= 0;

        if (rolesLoaded) {
            this.setState({
                roles,
                fetching: !newUser,
            });
        }

        if (newUser) {
            this.setState({
                user: {
                    id: "",
                    userName: "",
                    authenticated: false,
                    email: "",
                    roles: [],
                },
            });

            return;
        }

        this.setState(
            {
                fetching: true,
                success: false,
            },
            async () => {
                const { success: userLoaded, data: user } = await fetchUser(id);

                const success = userLoaded;

                this.setState({
                    success,
                    fetching: false,
                    user: success ? user : null,
                });
            },
        );
    }

    public render() {
        const {
            fetching,
            success,
            user,
            roles,
            message,
            password,
            repeatedPassword,
        } = this.state;

        if (fetching) {
            return (
                <div className={"container-fluid"}>
                    <Spinner
                        className={centeredSpinnerCn()}
                        name={"circle"}
                        color={"black"}
                    />
                </div>
            );
        }

        if (!success || user === null) {
            return (
                <div className={"container"}>
                    <div className={"row mt-4 mb-4"}>
                        <h1>{this.props.Admin_Dashboard_Users_UserNotFound}</h1>
                    </div>

                    <div className={"row mt-4 mb-4"}>
                        <Link to={"/user/list"} className={"btn btn-default"}>
                            {this.props.Admin_Dashboard_Users_UserList}
                        </Link>
                    </div>
                </div>
            );
        }

        const options = roles.map((r) => ({
            label: r.name as string,
            value: r.id,
        }));

        const isNewUser = !user.id;

        const selectedUserRoles = user.roles.map((r) => ({
            label: r.name as string,
            value: r.id,
        }));

        return (
            <div className="container mt-4">
                <AdminHeader
                    title={
                        this.state.user && this.state.user.id !== ""
                            ? this.props.Admin_Dashboard_Users_Edit
                            : this.props.Admin_Dashboard_Users_New
                    }
                />
                <form
                    className={"w-50"}
                    role={"form"}
                    autoComplete={"off"}
                    onSubmit={this.onSubmit}>
                    <div className={"form-group"}>
                        <label htmlFor={"user-email"}>
                            {this.props.Admin_Dashboard_Users_Email}
                        </label>

                        <input
                            className={"form-control"}
                            disabled={
                                this.state.user && this.state.user.softDeleted
                                    ? true
                                    : false
                            }
                            type={"email"}
                            id={"user-email"}
                            value={user.email}
                            required={true}
                            onChange={this.changeEmail}
                        />
                    </div>

                    <div className={"form-group"}>
                        <label htmlFor={"user-userName"}>
                            {this.props.Admin_Dashboard_Users_Username}
                        </label>

                        <input
                            className={"form-control"}
                            disabled={
                                this.state.user && this.state.user.softDeleted
                                    ? true
                                    : false
                            }
                            type={"text"}
                            id={"user-userName"}
                            value={user.userName}
                            required={true}
                            onChange={this.changeUserName}
                        />
                    </div>

                    {isNewUser && (
                        <React.Fragment>
                            <div
                                key={"new-user-passwword"}
                                className={"form-group"}>
                                <label htmlFor={"user-password"}>
                                    {this.props.Admin_Dashboard_Users_Password}
                                </label>

                                <input
                                    className={"form-control"}
                                    type={"password"}
                                    id={"user-password"}
                                    value={password}
                                    required={true}
                                    onChange={this.changePassword}
                                />
                            </div>

                            <div
                                key={"new-user-password-repeated"}
                                className={"form-group"}>
                                <label htmlFor={"user-password-repeated"}>
                                    {
                                        this.props
                                            .Admin_Dashboard_Users_RepeatPassword
                                    }
                                </label>

                                <input
                                    className={"form-control"}
                                    type={"password"}
                                    id={"user-password-repeated"}
                                    value={repeatedPassword}
                                    required={true}
                                    onChange={this.changeRepeatedPassword}
                                />
                            </div>
                        </React.Fragment>
                    )}

                    <div className={"form-group"}>
                        <label htmlFor={"user-roles"}>
                            {this.props.Admin_Dashboard_Users_Roles}
                        </label>

                        <Select
                            id={"user-roles"}
                            className={"w-100 p-0"}
                            closeMenuOnSelect={false}
                            isMulti={true}
                            onChange={this.changeRoles}
                            options={options}
                            placeholder={
                                this.props.Admin_Dashboard_Users_SelectRoles
                            }
                            value={selectedUserRoles}
                            isDisabled={
                                this.state.user && this.state.user.softDeleted
                                    ? true
                                    : false
                            }
                        />
                    </div>

                    {message !== null && (
                        <UserErrorMessage errorKey={message}></UserErrorMessage>
                    )}

                    <div className={"form-group"}>
                        <button
                            type={"submit"}
                            className={`btn mr-2 mb-2 ${buttonStyles.buttonBlue}`}
                            disabled={
                                this.state.user && this.state.user.softDeleted
                                    ? true
                                    : false
                            }>
                            {this.props.Admin_Dashboard_Users_Save}
                        </button>
                        {this.state.user && this.state.user.id !== "" && (
                            <button
                                className={`btn mr-2 mb-2 ${buttonStyles.buttonRed}`}
                                onClick={this.changeConfirmSoftDelete}
                                disabled={
                                    this.state.user &&
                                    this.state.user.softDeleted
                                        ? true
                                        : false
                                }>
                                {this.props.Admin_Dashboard_Users_Inactivate}
                            </button>
                        )}
                        {this.state.user && this.state.user.id !== "" && (
                            <button
                                className={`btn mr-2 mb-2 ${buttonStyles.buttonRed}`}
                                onClick={this.changeConfirmDelete}
                                disabled={
                                    this.state.user &&
                                    this.state.user.id ===
                                        this.props.loggedInUserId
                                        ? true
                                        : false
                                }>
                                {this.props.Admin_Dashboard_Users_Delete}
                            </button>
                        )}
                    </div>
                </form>

                <ConfirmationModal
                    isOpen={this.state.confirmDelete}
                    title={this.props.Admin_Dashboard_Users_Confirmation}
                    message={this.props.Admin_Dashboard_Users_DeletionMessage}
                    content={
                        <div>
                            <button
                                className={`btn mr-2 mb-2 ${buttonStyles.buttonBlue}`}
                                onClick={this.deleteUser}>
                                {this.props.Admin_Dashboard_Users_Confirm}
                            </button>
                            <button
                                className={`btn mb-2 ${buttonStyles.buttonGrey}`}
                                onClick={this.changeConfirmDelete}>
                                {this.props.Admin_Dashboard_Users_Cancel}
                            </button>
                        </div>
                    }></ConfirmationModal>
                <ConfirmationModal
                    isOpen={this.state.confirmSoftDelete}
                    title={this.props.Admin_Dashboard_Users_Confirmation}
                    message={
                        this.props.Admin_Dashboard_Users_InactivationMessage
                    }
                    content={
                        <div>
                            <button
                                className={`btn mr-2 mb-2 ${buttonStyles.buttonBlue}`}
                                onClick={this.softDeleteUser}>
                                {this.props.Admin_Dashboard_Users_Confirm}
                            </button>
                            <button
                                className={`btn mb-2 ${buttonStyles.buttonGrey}`}
                                onClick={this.changeConfirmSoftDelete}>
                                {this.props.Admin_Dashboard_Users_Cancel}
                            </button>
                        </div>
                    }></ConfirmationModal>
            </div>
        );
    }
}

const RoutedEditUser = withRouter(EditUser);

const mapStateToProps = (state: ApplicationState) => ({
    loggedInUserId: getId(state),
    Admin_Dashboard_Users: getResource(state, {
        key: "Admin_Dashboard_Users",
    }),
    Admin_Dashboard_Users_Username: getResource(state, {
        key: "Admin_Dashboard_Users_Username",
    }),
    Admin_Dashboard_Users_Email: getResource(state, {
        key: "Admin_Dashboard_Users_Email",
    }),
    Admin_Dashboard_Users_Roles: getResource(state, {
        key: "Admin_Dashboard_Users_Roles",
    }),
    Admin_Dashboard_Users_New: getResource(state, {
        key: "Admin_Dashboard_Users_New",
    }),
    Admin_Dashboard_Users_Edit: getResource(state, {
        key: "Admin_Dashboard_Users_Edit",
    }),
    Admin_Dashboard_Users_Password: getResource(state, {
        key: "Admin_Dashboard_Users_Password",
    }),
    Admin_Dashboard_Users_RepeatPassword: getResource(state, {
        key: "Admin_Dashboard_Users_RepeatPassword",
    }),
    Admin_Dashboard_Users_Save: getResource(state, {
        key: "Admin_Dashboard_Users_Save",
    }),
    Admin_Dashboard_Users_SelectRoles: getResource(state, {
        key: "Admin_Dashboard_Users_SelectRoles",
    }),
    Admin_Dashboard_Users_Confirmation: getResource(state, {
        key: "Admin_Dashboard_Users_Confirmation",
    }),
    Admin_Dashboard_Users_DeletionMessage: getResource(state, {
        key: "Admin_Dashboard_Users_DeletionMessage",
    }),
    Admin_Dashboard_Users_InactivationMessage: getResource(state, {
        key: "Admin_Dashboard_Users_InactivationMessage",
    }),
    Admin_Dashboard_Users_Confirm: getResource(state, {
        key: "Admin_Dashboard_Users_Confirm",
    }),
    Admin_Dashboard_Users_Cancel: getResource(state, {
        key: "Admin_Dashboard_Users_Cancel",
    }),
    Admin_Dashboard_Users_Delete: getResource(state, {
        key: "Admin_Dashboard_Users_Delete",
    }),
    Admin_Dashboard_Users_Inactivate: getResource(state, {
        key: "Admin_Dashboard_Users_Inactivate",
    }),
    Admin_Dashboard_Users_UserNotFound: getResource(state, {
        key: "Admin_Dashboard_Users_UserNotFound",
    }),
    Admin_Dashboard_Users_UserList: getResource(state, {
        key: "Admin_Dashboard_Users_UserList",
    }),
    Admin_Dashboard_Users_UserWasSaved: getResource(state, {
        key: "Admin_Dashboard_Users_UserWasSaved",
    }),
    Admin_Dashboard_Users_UserWasDeleted: getResource(state, {
        key: "Admin_Dashboard_Users_UserWasDeleted",
    }),
    Admin_Dashboard_Users_UserWasInactivated: getResource(state, {
        key: "Admin_Dashboard_Users_UserWasInactivated",
    }),
});

const ConnectedEditUser = connect(mapStateToProps)(RoutedEditUser);

export default ConnectedEditUser;
